import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import { ChannelToggles, channelLabel, type Channel } from '../../channels';
import { orgPath } from '../../nav';
import { useOrg } from '../Org';
import type { SkillSummary } from '../Skills';

/** Who may open this org, as which Salesforce user, and how. */
export function OrgGrants() {
  const { tenantId, orgId, detail, orgDisabled, members, busy, act } = useOrg();
  const [identityId, setIdentityId] = useState('');
  const [userId, setUserId] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [channels, setChannels] = useState<Channel[]>(['browser']);
  const [skills, setSkills] = useState<SkillSummary[]>([]);

  const selected = detail.identities.find((i) => i.id === identityId) ?? null;

  // A grant can only name what the org allows and what the identity's licence can serve. An
  // integration user disappears from the web row here rather than failing later at mint time.
  //
  // Automation is left out entirely: this form grants to a person, and an automation channel belongs to
  // an automation, which is a different kind of principal altogether. Offering it here could only ever
  // produce a grant the API refuses.
  const grantable: Channel[] = detail.org.channels.filter(
    (c) => c !== 'automation' && (!selected || selected.channels.includes(c)),
  );

  // Keep the tick boxes and the state they stand for from drifting apart. A blocked row renders unchecked
  // whatever the state says, so without this a default of Web survives invisibly behind a disabled box and
  // gets posted for an identity that cannot serve it.
  useEffect(() => {
    setChannels((prev) => {
      const kept = prev.filter((c) => grantable.includes(c));
      if (kept.length === prev.length && kept.length) return prev;
      return kept.length ? kept : grantable.slice(0, 1);
    });
    // grantable is derived from these two, and depending on the array itself would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityId, detail.org.channels]);

  // Only the active ones: a disabled skill cannot be run, so offering it would be offering nothing.
  useEffect(() => {
    void (async () => {
      try {
        // This org's skills, not the workspace's: a grant may only carry skills belonging to the org it
        // is a grant on, so anything else here would be an option the save refuses.
        const page = await api<{ skills: SkillSummary[] }>(`/api/tenants/${tenantId}/orgs/${orgId}/skills`);
        setSkills(page.skills.filter((s) => s.status === 'active'));
      } catch {
        setSkills([]);
      }
    })();
  }, [tenantId, orgId]);

  /**
   * A grant may carry agent or skill, never both.
   *
   * The database refuses it too, and it has to: a grant holding both lets an agent mint a token and walk
   * around the skill layer entirely. Here it is a nicer refusal — ticking one unticks the other, so the
   * choice reads as the either-or it is rather than as a validation error after the fact.
   */
  function setChannelsExclusive(next: Channel[]) {
    const added = next.find((c) => !channels.includes(c));
    if (added === 'agent') return setChannels(next.filter((c) => c !== 'skill'));
    if (added === 'skill') return setChannels(next.filter((c) => c !== 'agent'));
    setChannels(next);
  }

  function addGrant(e: FormEvent) {
    e.preventDefault();
    void act(
      () =>
        api(`/api/tenants/${tenantId}/orgs/${orgId}/grants`, {
          method: 'POST',
          body: { identityId, userId, endsAt: endsAt || undefined, channels },
        }),
      'Access granted.',
    ).then(() => setEndsAt(''));
  }

  const usable = detail.identities.filter((i) => i.verified && !i.disabledAt);

  return (
    <>
      <div className="card flush">
        <table>
          <thead>
            <tr>
              <th>Person</th>
              <th>As</th>
              <th>How</th>
              <th>Until</th>
              <th>Granted by</th>
              <th className="actions-cell"></th>
            </tr>
          </thead>
          <tbody>
            {detail.grants.length === 0 && (
              <tr>
                <td colSpan={6} className="empty">
                  Nobody has access to this org yet.
                </td>
              </tr>
            )}
            {detail.grants.flatMap((g) => [
              <tr key={g.id}>
                <td>
                  {g.userName || g.userEmail}
                  <br />
                  <span className="small muted">{g.userEmail}</span>
                </td>
                <td className="small mono">{g.username}</td>
                <td className="small">{g.channels.map(channelLabel).join(', ')}</td>
                <td>{g.endsAt ? new Date(g.endsAt).toLocaleString() : 'permanent'}</td>
                <td className="small muted">{g.grantedByEmail}</td>
                <td className="actions-cell">
                  <button
                    className="danger"
                    disabled={busy}
                    onClick={() =>
                      act(
                        () => api(`/api/tenants/${tenantId}/orgs/${orgId}/grants/${g.id}`, { method: 'DELETE' }),
                        'Grant revoked. Any live sessions end within a minute.',
                      )
                    }
                  >
                    Revoke
                  </button>
                </td>
              </tr>,
              g.channels.includes('skill') && (
                <tr key={`${g.id}-skills`}>
                  <td colSpan={6} style={{ paddingTop: 0 }}>
                    <span className="small muted">
                      {skills.length
                        ? `Can run every skill on this org: ${skills.map((sk) => sk.name).join(', ')}.`
                        : 'This org has no skills yet, so this grant can reach nothing until one is made.'}{' '}
                      <Link to={orgPath(tenantId, orgId, 'skills')}>Skills</Link>
                    </span>
                  </td>
                </tr>
              ),
            ])}
          </tbody>
        </table>
      </div>

      {!orgDisabled && usable.length === 0 && (
        <div className="card">
          <p className="small muted" style={{ margin: 0 }}>
            There is no verified Salesforce user to grant yet. Add one on the{' '}
            <Link to={orgPath(tenantId, orgId)}>Salesforce users</Link> page first — a grant names a
            Salesforce user to act as, so there has to be one.
          </p>
        </div>
      )}

      {!orgDisabled && usable.length > 0 && (
        <div className="card">
          <h2>Grant access</h2>
          <form onSubmit={addGrant}>
            <label>
              Who
              <select value={userId} onChange={(e) => setUserId(e.target.value)} required>
                <option value="">Choose a person…</option>
                {members.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.displayName || m.email}
                  </option>
                ))}
              </select>
            </label>
            <label>
              As which Salesforce user
              <select value={identityId} onChange={(e) => setIdentityId(e.target.value)} required>
                <option value="">Choose a Salesforce user…</option>
                {usable.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.username}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Until (optional)
              <input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
              <span className="small muted" style={{ fontWeight: 400 }}>
                Leave it empty for permanent access.
              </span>
            </label>
            <div>
              <p style={{ margin: 0, fontWeight: 500 }}>How may they connect?</p>
              <ChannelToggles
                value={channels}
                onChange={setChannelsExclusive}
                available={grantable}
                show={['browser', 'cli', 'agent', 'skill']}
                ceilingName={
                  identityId && selected && selected.channels.length < detail.org.channels.length
                    ? `this user's Salesforce licence`
                    : 'this org'
                }
                disabled={busy}
              />
              <p className="small muted" style={{ margin: 0 }}>
                CLI and agent tokens are short-lived and re-minted; revoking or an end date ends live sessions
                within a minute. AI agent and Omni MCP are alternatives, not a pair: one hands over a token, the
                other keeps it here and runs the query for them. A brokered grant can run every skill on this
                org; there is nothing to attach.
              </p>
            </div>
            <button type="submit" className="block" disabled={busy || !channels.length}>
              Grant access
            </button>
            {!channels.length && (
              <p className="small muted" style={{ margin: '-8px 0 0' }}>
                Choose at least one way to connect.
              </p>
            )}
          </form>
        </div>
      )}
    </>
  );
}
